<template>
  <ValidationObserver ref="employeeFormObserver" v-slot="{ handleSubmit }">
    <b-form @submit.prevent="handleSubmit(submit)">
      <b-row>
        <b-col sm="6" offset-sm="3">
          <div class="row justify-content-center">
            <avatar-input
              v-model="model.image"
              :greyscale.sync="model.imageGreyscale"
              @error="onAvatarInputError"
            ></avatar-input>
          </div>
          <ValidationProvider
            name="name"
            rules="required|min:1"
            v-slot="{ errors }"
          >
            <fg-input
              :label="$t('message.employeeName')"
              v-model="model.name"
              :placeholder="$t('message.employeeNamePlaceholder')"
              :error="errors[0]"
              required
            />
          </ValidationProvider>
          <fg-input
            :label="$t('message.employeeDescription')"
            v-model="model.description"
            :placeholder="$t('message.employeeDescriptionPlaceholder')"
          />
          <fg-input
            :label="$t('message.employeeTipUrl')"
            v-model="model.tipUrl"
            :description="$t('message.employeeTipUrlDescription')"
          >
          </fg-input>
          <fg-input class="mt-4">
            <n-checkbox v-model="model.availableForOnlineBookings">
              {{ $t("message.employeeAvailableForOnlineBookings") }}
            </n-checkbox>
          </fg-input>
        </b-col>
      </b-row>
      <br />
      <b-alert
        v-if="onlineWithoutWorkingDays"
        show
        variant="warning"
      >
        {{ $t("message.employeeRosterOnlineWithoutDays") }}
      </b-alert>
      <h6 class="mb-3">{{ $t("message.employeeRosterHeading") }}</h6>
      <p class="text-muted">
        {{ $t("message.employeeRosterUsualHoursHint") }}
      </p>
      <p v-if="isNewEmployee" class="text-muted">
        {{ $t("message.employeeRosterDefaultsHint") }}
      </p>
      <b-row
        v-for="day in days"
        :key="day"
        class="d-flex align-items-center pb-3"
      >
        <b-col sm="12" class="mb-1">
          <b-checkbox
            :disabled="!(shop.daysOpen && shop.daysOpen[day])"
            v-model="model.rosterDefaultWorkDays[day]"
            @change="value => handleRosterDayChecked(day, value)"
          >
            {{ day | formatDayReadable }}
          </b-checkbox>
        </b-col>
        <b-col sm="12" md="3" class="mb-1">
          <vue-ctk-date-time-picker
            :id="`rosterDefaults__${day}__startTime`"
            :label="$t('message.resourceRosterWorkStartLabel')"
            v-model="model.rosterDefaultWorkStartTimes[day]"
            :format="TIME_FORMAT_CODE"
            :formatted="TIME_FORMAT_READABLE"
            :only-time="true"
            :right="true"
            :minute-interval="5"
            required
            no-clear-button
            no-keyboard
            :disabled="!model.rosterDefaultWorkDays[day]"
          ></vue-ctk-date-time-picker>
        </b-col>
        <b-col sm="12" md="3" class="mb-1">
          <vue-ctk-date-time-picker
            :id="`rosterDefaults__${day}__endTime`"
            :label="$t('message.resourceRosterWorkEndLabel')"
            v-model="model.rosterDefaultWorkEndTimes[day]"
            :format="TIME_FORMAT_CODE"
            :formatted="TIME_FORMAT_READABLE"
            :only-time="true"
            :right="true"
            :minute-interval="5"
            :disabled-hours="
              hoursOutside(model.rosterDefaultWorkStartTimes[day], '23:00')
            "
            required
            no-clear-button
            no-keyboard
            :disabled="!model.rosterDefaultWorkDays[day]"
          ></vue-ctk-date-time-picker>
        </b-col>
        <b-col sm="12" md="3" class="mb-1">
          <vue-ctk-date-time-picker
            :id="`rosterDefaults__${day}__lunchStart`"
            :label="$t('message.resourceRosterLunchStartLabel')"
            :value="model.rosterDefaultLunchStartTimes[day]"
            :format="TIME_FORMAT_CODE"
            :formatted="TIME_FORMAT_READABLE"
            :only-time="true"
            :right="true"
            :minute-interval="5"
            :disabled-hours="
              hoursOutside(
                model.rosterDefaultWorkStartTimes[day],
                model.rosterDefaultWorkEndTimes[day]
              )
            "
            :disabled="!model.rosterDefaultWorkDays[day]"
            no-keyboard
            @input="value => handleRosterLunchStartTimeInput(day, value)"
          ></vue-ctk-date-time-picker>
        </b-col>
        <b-col sm="12" md="3" class="mb-1">
          <vue-ctk-date-time-picker
            :id="`rosterDefaults__${day}__lunchEnd`"
            :label="$t('message.resourceRosterLunchEndLabel')"
            :value="model.rosterDefaultLunchEndTimes[day]"
            :format="TIME_FORMAT_CODE"
            :formatted="TIME_FORMAT_READABLE"
            :only-time="true"
            :right="true"
            :minute-interval="5"
            :disabled-hours="
              hoursOutside(
                model.rosterDefaultLunchStartTimes[day],
                model.rosterDefaultWorkEndTimes[day]
              )
            "
            :disabled="
              !model.rosterDefaultWorkDays[day] ||
                !model.rosterDefaultLunchStartTimes[day]
            "
            no-keyboard
            required
            @input="value => handleRosterLunchEndTimeInput(day, value)"
          ></vue-ctk-date-time-picker>
        </b-col>
      </b-row>
      <br />
      <n-button type="primary" native-type="submit" :busy="saving">
        {{ $t("message.save") }}
      </n-button>
      <router-link tag="a" to="/management/employees">
        <n-button class="ml-1" type="secondary" simple>{{
          $t("message.cancel")
        }}</n-button>
      </router-link>
    </b-form>
  </ValidationObserver>
</template>
<script>
import _ from "lodash";
import moment from "moment";
import { ValidationProvider, ValidationObserver } from "vee-validate";
import { mapGetters } from "vuex";
import { Checkbox, AvatarInput } from "@/components";
import {
  TIME_FORMAT_CODE,
  TIME_FORMAT_READABLE,
  formatTimeReadable,
  hoursOutside
} from "@/helpers/time";
import {
  defaultRosterFromShop,
  isOnlineWithoutWorkingDays
} from "@/helpers/rosterDefaults";

export default {
  components: {
    ValidationProvider,
    ValidationObserver,
    [Checkbox.name]: Checkbox,
    AvatarInput
  },
  filters: {
    formatTimeReadable,
    formatDayReadable: _.startCase
  },
  props: {
    employee: {
      type: Object,
      default: () => ({
        availableForOnlineBookings: true,
        description: null,
        enabled: true,
        image: null,
        /** Whether the employee photo is stored as black & white */
        imageGreyscale: false,
        name: null,
        tipUrl: null,
        rosterDefaultLunchEndTimes: {},
        rosterDefaultLunchStartTimes: {},
        rosterDefaultWorkDays: {},
        rosterDefaultWorkEndTimes: {},
        rosterDefaultWorkStartTimes: {}
      })
    },
    saving: Boolean
  },
  data() {
    return {
      TIME_FORMAT_CODE,
      TIME_FORMAT_READABLE,
      model: {},
      days: [
        "monday",
        "tuesday",
        "wednesday",
        "thursday",
        "friday",
        "saturday",
        "sunday"
      ]
    };
  },
  computed: {
    ...mapGetters({
      shop: "shops/shop"
    }),
    isNewEmployee() {
      return !this.employee || !this.employee._id;
    },
    onlineWithoutWorkingDays() {
      return isOnlineWithoutWorkingDays(this.model);
    }
  },
  methods: {
    hoursOutside,
    onAvatarInputError({ message }) {
      this.$notify({
        type: "error",
        title: this.$t("message.error"),
        text: message
      });
    },
    handleRosterDayChecked(day, checked) {
      if (!checked) return;

      if (!this.model.rosterDefaultWorkStartTimes[day]) {
        this.$set(this.model.rosterDefaultWorkStartTimes,day ,this.shop.openingTimes[day]);
      }

      if (!this.model.rosterDefaultWorkEndTimes[day]) {
        this.$set(this.model.rosterDefaultWorkEndTimes,day ,this.shop.closingTimes[day]);
      }
    },
    setRosterLunchStartTime(day, lunchStartTime) {
      this.$set(this.model.rosterDefaultLunchStartTimes, day, lunchStartTime);
    },
    setRosterLunchEndTime(day, lunchEndTime) {
      this.$set(this.model.rosterDefaultLunchEndTimes, day, lunchEndTime);
    },
    handleRosterLunchStartTimeInput(day, lunchStartTime) {
      this.setRosterLunchStartTime(day, lunchStartTime);

      if (!lunchStartTime) {
        this.setRosterLunchEndTime(day, null);
        return;
      }

      if (!this.model.rosterDefaultLunchEndTimes[day]) {
        const lunchEndTime = moment(lunchStartTime, TIME_FORMAT_CODE)
          .add(1, "hour")
          .format(TIME_FORMAT_CODE);

        this.$set(this.model.rosterDefaultLunchEndTimes, day, lunchEndTime);
      }
    },
    handleRosterLunchEndTimeInput(day, lunchEndTime) {
      this.setRosterLunchEndTime(day, lunchEndTime);

      if (!lunchEndTime) {
        this.setRosterLunchStartTime(day, null);
      }
    },
    submit() {
      this.$emit("submit", this.model);
    }
  },
  created() {
    const cloned = _.cloneDeep(this.employee);
    const rosterDefaults =
      this.employee && this.employee._id
        ? {}
        : defaultRosterFromShop(this.shop);
    this.model = {
      rosterDefaultLunchEndTimes: {},
      rosterDefaultLunchStartTimes: {},
      rosterDefaultWorkDays: {},
      rosterDefaultWorkEndTimes: {},
      rosterDefaultWorkStartTimes: {},
      imageGreyscale: false,
      availableForOnlineBookings: true,
      ...cloned,
      ...rosterDefaults
    };
    // Legacy records may omit the flag — treat missing as off
    if (typeof this.model.imageGreyscale !== "boolean") {
      this.model.imageGreyscale = false;
    }
  }
};
</script>
